/**
 * CommentOverlay — canvas layer for the comments feature.
 *
 * Renders, inside the Excalidraw children tree (above the canvases, below
 * the LayerUI islands):
 *
 * - a pin per open comment, anchored to its scene position
 * - a full-canvas catcher while comment placement is armed (from the
 *   sidebar's "Add" button), turning the next canvas click into a draft
 * - a small composer popover at the draft position to write the comment
 *
 * Positions are recomputed on pan/zoom via `useExcalidrawStateValue`.
 */

import {
  DEFAULT_SIDEBAR,
  sceneCoordsToViewportCoords,
  viewportCoordsToSceneCoords,
} from "@excalidraw/common";
import {
  useExcalidrawAPI,
  useExcalidrawStateValue,
} from "@excalidraw/excalidraw";
import { messageCircleIcon } from "@excalidraw/excalidraw/components/icons";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { useAtom } from "../app-jotai";

import {
  addComment,
  commentsAtom,
  commentDraftAtom,
  commentPlacementAtom,
  highlightedCommentIdAtom,
} from "./CommentManager";

import "./CommentOverlay.scss";

const VIEWPORT_STATE_PROPS = [
  "scrollX",
  "scrollY",
  "zoom",
  "offsetLeft",
  "offsetTop",
  "width",
  "height",
] as const;

// ---------------------------------------------------------------------------
// draft composer popover
// ---------------------------------------------------------------------------

const DraftComposer = ({
  left,
  top,
  onSubmit,
  onCancel,
}: {
  left: number;
  top: number;
  onSubmit: (message: string) => void;
  onCancel: () => void;
}) => {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const submit = () => {
    if (value.trim()) {
      onSubmit(value);
    }
  };

  return (
    <div
      className="comment-overlay__composer"
      style={{ left, top }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <textarea
        ref={textareaRef}
        value={value}
        placeholder="Add a comment…"
        rows={3}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          // keep global canvas shortcuts from firing while typing
          event.stopPropagation();
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            submit();
          } else if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
      />
      <div className="comment-overlay__composer-actions">
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="comment-overlay__composer-submit"
          disabled={!value.trim()}
          onClick={submit}
        >
          Comment
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// overlay
// ---------------------------------------------------------------------------

export const CommentOverlay = () => {
  const excalidrawAPI = useExcalidrawAPI();
  // re-render on pan/zoom/resize so pins track their scene positions
  const viewportState = useExcalidrawStateValue([...VIEWPORT_STATE_PROPS]);

  const [comments] = useAtom(commentsAtom);
  const [placementActive, setPlacementActive] = useAtom(commentPlacementAtom);
  const [draft, setDraft] = useAtom(commentDraftAtom);
  const [highlightedId, setHighlightedId] = useAtom(highlightedCommentIdAtom);

  const rootRef = useRef<HTMLDivElement>(null);

  // Escape cancels placement / draft
  useEffect(() => {
    if (!placementActive && !draft) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPlacementActive(false);
        setDraft(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [placementActive, draft, setPlacementActive, setDraft]);

  if (!viewportState) {
    return null;
  }

  // the overlay's own origin within the viewport, so client coordinates can
  // be converted to overlay-relative ones regardless of where the host app
  // mounts the editor
  const rootRect = rootRef.current?.getBoundingClientRect();
  const rootOffsetX = rootRect?.left ?? 0;
  const rootOffsetY = rootRect?.top ?? 0;

  const toOverlayPosition = (sceneX: number, sceneY: number) => {
    const { x, y } = sceneCoordsToViewportCoords(
      { sceneX, sceneY },
      viewportState,
    );
    return { left: x - rootOffsetX, top: y - rootOffsetY };
  };

  const openComments = comments.filter((comment) => comment.resolvedAt == null);

  const focusCommentInSidebar = (commentId: string) => {
    setHighlightedId(commentId);
    excalidrawAPI?.updateScene({
      appState: {
        openSidebar: { name: DEFAULT_SIDEBAR.name, tab: "comments" },
      },
    });
  };

  return (
    <div ref={rootRef} className="comment-overlay">
      {/* placement catcher — armed from the sidebar's "Add" button */}
      {placementActive && (
        <div
          className="comment-overlay__catcher"
          style={{
            left: viewportState.offsetLeft - rootOffsetX,
            top: viewportState.offsetTop - rootOffsetY,
            width: viewportState.width,
            height: viewportState.height,
          }}
          data-testid="comment-placement-catcher"
          onPointerDown={(event) => {
            event.preventDefault();
            const { x, y } = viewportCoordsToSceneCoords(
              { clientX: event.clientX, clientY: event.clientY },
              viewportState,
            );
            setPlacementActive(false);
            setDraft({ x, y });
          }}
        />
      )}

      {/* comment pins */}
      {!placementActive &&
        openComments.map((comment) => {
          const { left, top } = toOverlayPosition(comment.x, comment.y);
          return (
            <button
              key={comment.id}
              type="button"
              className={clsx("comment-overlay__pin", {
                "comment-overlay__pin--highlighted":
                  highlightedId === comment.id,
              })}
              style={{ left, top }}
              title={`${comment.author}: ${comment.message}`}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => focusCommentInSidebar(comment.id)}
            >
              {messageCircleIcon}
              {comment.replies.length > 0 && (
                <span className="comment-overlay__pin-badge">
                  {comment.replies.length + 1}
                </span>
              )}
            </button>
          );
        })}

      {/* draft pin + composer */}
      {draft &&
        (() => {
          const { left, top } = toOverlayPosition(draft.x, draft.y);
          return (
            <>
              <span
                className="comment-overlay__pin comment-overlay__pin--draft"
                style={{ left, top }}
              >
                {messageCircleIcon}
              </span>
              <DraftComposer
                left={left}
                top={top}
                onSubmit={(message) => {
                  const comment = addComment({
                    x: draft.x,
                    y: draft.y,
                    message,
                  });
                  setDraft(null);
                  if (comment) {
                    focusCommentInSidebar(comment.id);
                  }
                }}
                onCancel={() => setDraft(null)}
              />
            </>
          );
        })()}
    </div>
  );
};
